import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu as MenuIcon, X, Globe, Lock } from "lucide-react";
import { useApp } from "../AppContext";
import { Button } from "./ui/button";
import DominicanFlag from "./DominicanFlag";

const Header = () => {
    const { t, lang, changeLang, cartCount, setCartOpen, branding } = useApp();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goSection = (id) => {
        setMobileOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }, 50);
        } else {
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }
    };

    const navItems = [
        { id: "menu", label: t.nav.menu },
        { id: "chefs", label: t.nav.chefs },
        { id: "loyalty", label: lang === "es" ? "Fidelidad" : "Loyalty" },
        { id: "catering", label: t.catering.tag },
        { id: "contact", label: t.nav.contact },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-[var(--banner-h,52px)] left-0 right-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "bg-earth-50/90 backdrop-blur-xl border-b border-earth-200 py-3"
                    : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <div
                        className="relative w-11 h-11 rounded-full flex items-center justify-center text-white font-serif font-bold text-base shadow-md group-hover:rotate-6 transition-transform"
                        style={{ background: branding.brand_color_primary }}
                    >
                        {branding.brand_initials || "DD"}
                        {branding.decorations_enabled && (
                            <DominicanFlag className="absolute -bottom-1 -right-1 w-5 h-3.5 rounded-sm shadow ring-1 ring-white" />
                        )}
                    </div>
                    <div className="hidden sm:block leading-tight">
                        <div className="font-serif font-bold text-earth-900 text-base">
                            {branding.brand_name}
                        </div>
                        <div
                            className="font-accent text-sm -mt-1"
                            style={{ color: branding.brand_color_primary }}
                        >
                            {branding.brand_tagline}
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {navItems.map((n) => (
                        <button
                            key={n.id}
                            data-testid={`nav-${n.id}-btn`}
                            onClick={() => goSection(n.id)}
                            className="text-sm font-semibold text-earth-700 hover:text-terracotta-600 transition-colors relative group"
                        >
                            {n.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-terracotta-500 group-hover:w-full transition-all duration-300" />
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        data-testid="lang-toggle-btn"
                        onClick={() => changeLang(lang === "es" ? "en" : "es")}
                        className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full border border-earth-200 hover:border-terracotta-500 text-earth-800 text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                        <Globe className="w-3.5 h-3.5" />
                        {lang === "es" ? "EN" : "ES"}
                    </button>

                    <Link
                        to="/admin/login"
                        data-testid="header-admin-link"
                        title={t.nav.admin}
                        className="hidden sm:flex items-center justify-center p-2 rounded-full border border-earth-200 hover:border-terracotta-500 hover:text-terracotta-600 text-earth-700 transition-colors"
                        aria-label="admin"
                    >
                        <Lock className="w-3.5 h-3.5" />
                    </Link>

                    <button
                        data-testid="cart-open-btn"
                        onClick={() => setCartOpen(true)}
                        className="relative p-2.5 rounded-full bg-earth-100 hover:bg-earth-200 transition-colors"
                        aria-label="cart"
                    >
                        <ShoppingBag className="w-5 h-5 text-earth-800" />
                        {cartCount > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-1 -right-1 bg-terracotta-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1"
                            >
                                {cartCount}
                            </span>
                        )}
                    </button>

                    <Button
                        data-testid="header-order-btn"
                        onClick={() => goSection("order")}
                        className="hidden md:inline-flex bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full px-6"
                    >
                        {t.nav.order}
                    </Button>

                    <button
                        data-testid="mobile-menu-btn"
                        onClick={() => setMobileOpen((o) => !o)}
                        className="lg:hidden p-2 rounded-full hover:bg-earth-100"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="lg:hidden bg-earth-50 border-t border-earth-200 mt-3">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {navItems.map((n) => (
                            <button
                                key={n.id}
                                data-testid={`mobile-nav-${n.id}-btn`}
                                onClick={() => goSection(n.id)}
                                className="text-left text-base font-semibold text-earth-800 py-2"
                            >
                                {n.label}
                            </button>
                        ))}
                        <button
                            data-testid="mobile-lang-toggle-btn"
                            onClick={() => changeLang(lang === "es" ? "en" : "es")}
                            className="text-left text-base font-semibold text-terracotta-600 py-2"
                        >
                            {lang === "es" ? "English" : "Español"}
                        </button>
                        <button
                            data-testid="mobile-order-btn"
                            onClick={() => goSection("order")}
                            className="btn-primary mt-2"
                        >
                            {t.nav.order}
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
